/**
 * เมนูลัดด้านล่างแชท LINE (rich menu)
 *
 * นิยามไว้ที่เดียวแล้วใช้ทั้งสองฝั่ง: หน้าเว็บใช้วาดรูปเมนู ส่วน Worker ใช้สร้าง
 * พื้นที่กดบน LINE ถ้าแยกกันเขียน ปุ่มที่เห็นกับปุ่มที่กดได้จะเลื่อนไม่ตรงกัน
 * ซึ่งเป็นบั๊กที่หายากมากเพราะทุกอย่าง "ดูปกติ"
 *
 * ขนาดที่ LINE กำหนด: กว้าง 2500 · สูง 1686 (เต็ม) หรือ 843 (ครึ่ง)
 */

export const RICH_MENU_WIDTH = 2500;
export const RICH_MENU_TALL = 1686;
export const RICH_MENU_SHORT = 843;

export interface RichMenuButton {
  /** ข้อความบนปุ่ม */
  label: string;
  /** อีโมจิที่ใช้แทนไอคอน — เบราว์เซอร์วาดได้เลยโดยไม่ต้องมีไฟล์รูป */
  icon: string;
  /**
   * เส้นทางใน LIFF เช่น `/today` — ระบบต่อกับ LIFF id ให้เอง
   * ถ้าเป็น `null` = ปุ่มส่งข้อความเข้าแชทแทนการเปิดหน้า
   */
  path: string | null;
  /** ข้อความที่ส่งเข้าแชทเมื่อ path เป็น null */
  text?: string;
}

export interface RichMenuLayout {
  key: RichMenuKey;
  /** ชื่อที่เห็นในคอนโซล LINE */
  name: string;
  /** ข้อความบนแถบที่ผู้ใช้กดเพื่อเปิดเมนู */
  chatBarText: string;
  height: number;
  columns: number;
  rows: number;
  buttons: RichMenuButton[];
  /** OA ไหนใช้เมนูนี้ */
  oa: 'staff' | 'public';
  description: string;
}

export type RichMenuKey = 'unlinked' | 'field_officer' | 'supervisor' | 'finance' | 'donor';

/**
 * เจ้าหน้าที่ที่ยังไม่ได้ผูกบัญชี — มีปุ่มเดียวโดยตั้งใจ
 * ถ้าใส่ปุ่มอื่นด้วย เขาจะกดแล้วเจอ 403 ซ้ำ ๆ โดยไม่รู้ว่าต้องผูกบัญชีก่อน
 */
const UNLINKED: RichMenuLayout = {
  key: 'unlinked',
  name: 'เจ้าหน้าที่ — ยังไม่ผูกบัญชี',
  chatBarText: 'เริ่มใช้งาน',
  height: RICH_MENU_SHORT,
  columns: 1,
  rows: 1,
  oa: 'staff',
  description: 'เมนูตั้งต้นของ OA เจ้าหน้าที่ ก่อนผูกบัญชี',
  buttons: [{ label: 'ผูกบัญชีเจ้าหน้าที่', icon: '🔗', path: '/link' }],
};

const FIELD_OFFICER: RichMenuLayout = {
  key: 'field_officer',
  name: 'เจ้าหน้าที่ภาคสนาม',
  chatBarText: 'เมนูเจ้าหน้าที่',
  height: RICH_MENU_TALL,
  columns: 3,
  rows: 2,
  oa: 'staff',
  description: 'ใช้กับ field_officer และ supervisor',
  buttons: [
    { label: 'งานวันนี้', icon: '📋', path: '/today' },
    { label: 'ค้นหาผู้รับความช่วยเหลือ', icon: '🔍', path: '/search' },
    { label: 'เคสของฉัน', icon: '📁', path: '/cases' },
    { label: 'ตารางนัดติดตาม', icon: '🗓️', path: '/today?tab=followup' },
    { label: 'คู่มือใช้งาน', icon: '📖', path: null, text: 'คู่มือ' },
    { label: 'ติดต่อสำนักงาน', icon: '☎️', path: null, text: 'ติดต่อเจ้าหน้าที่' },
  ],
};

const SUPERVISOR: RichMenuLayout = {
  ...FIELD_OFFICER,
  key: 'supervisor',
  name: 'หัวหน้างาน',
  description: 'เหมือนภาคสนาม แต่แยกไว้เผื่อปรับเมนูต่างกันภายหลัง',
};

const FINANCE: RichMenuLayout = {
  key: 'finance',
  name: 'ฝ่ายการเงิน',
  chatBarText: 'เมนูการเงิน',
  height: RICH_MENU_SHORT,
  columns: 3,
  rows: 1,
  oa: 'staff',
  description: 'ฝ่ายการเงินทำงานบนเว็บเป็นหลัก เมนูจึงสั้น',
  buttons: [
    { label: 'เคสรออนุมัติ', icon: '📁', path: '/cases' },
    { label: 'ค้นหาผู้รับความช่วยเหลือ', icon: '🔍', path: '/search' },
    { label: 'ติดต่อสำนักงาน', icon: '☎️', path: null, text: 'ติดต่อเจ้าหน้าที่' },
  ],
};

/**
 * OA สาธารณะให้บริการคนสองกลุ่มที่ต่างกันมาก: คนที่มาให้ กับคนที่มาขอ
 *
 * แยกเป็นคนละ OA จะดีกว่าในทางทฤษฎี แต่มูลนิธิเล็กดูแล OA เดียวยังแทบไม่ไหว
 * และคนที่มาขอความช่วยเหลือจะไม่รู้ว่าต้องไปเพิ่มเพื่อนอีกบัญชี — จึงรวมไว้
 * ที่เดียวโดยแบ่งเป็นสองแถวให้ชัด แถวบน "ให้" แถวล่าง "ขอ"
 *
 * ปุ่มฝั่งขอความช่วยเหลือ **ไม่มีปุ่มไหนต้องล็อกอินก่อน** เพราะผู้ขอส่วนใหญ่
 * เป็นผู้สูงอายุและใช้มือถือร่วมกับลูกหลาน การบังคับผูกบัญชีเท่ากับไม่ได้ใช้จริง
 */
const PUBLIC_MENU: RichMenuLayout = {
  key: 'donor',
  name: 'เมนูสาธารณะ — บริจาค / ขอความช่วยเหลือ',
  chatBarText: 'เมนู',
  height: RICH_MENU_TALL,
  columns: 3,
  rows: 2,
  oa: 'public',
  description:
    'เมนูเริ่มต้นของ OA สาธารณะ · แถวบนสำหรับผู้บริจาค แถวล่างสำหรับผู้ขอความช่วยเหลือ',
  buttons: [
    { label: 'บริจาค', icon: '❤️', path: '/donate' },
    { label: 'แจ้งโอนเงิน', icon: '🧾', path: '/slip' },
    { label: 'ใบอนุโมทนา', icon: '📜', path: '/history' },
    { label: 'ขอความช่วยเหลือ', icon: '🙏', path: '/help' },
    { label: 'ติดตามสถานะ', icon: '🔎', path: '/track' },
    { label: 'ติดต่อเจ้าหน้าที่', icon: '☎️', path: null, text: 'ติดต่อเจ้าหน้าที่' },
  ],
};

export const RICH_MENU_LAYOUTS: RichMenuLayout[] = [
  UNLINKED,
  FIELD_OFFICER,
  SUPERVISOR,
  FINANCE,
  PUBLIC_MENU,
];

export function richMenuLayout(key: RichMenuKey): RichMenuLayout | undefined {
  return RICH_MENU_LAYOUTS.find((l) => l.key === key);
}

/** พิกัดพื้นที่กดของปุ่มที่ index ที่กำหนด — ใช้ร่วมกันระหว่างรูปกับพื้นที่กดจริง */
export function richMenuBounds(
  layout: RichMenuLayout,
  index: number,
): { x: number; y: number; width: number; height: number } {
  const cellW = Math.floor(RICH_MENU_WIDTH / layout.columns);
  const cellH = Math.floor(layout.height / layout.rows);
  const col = index % layout.columns;
  const row = Math.floor(index / layout.columns);
  return {
    x: col * cellW,
    y: row * cellH,
    // ช่องขวาสุด/ล่างสุดกินเศษที่เหลือ ไม่งั้นจะมีแถบตายที่กดไม่ได้
    width: col === layout.columns - 1 ? RICH_MENU_WIDTH - col * cellW : cellW,
    height: row === layout.rows - 1 ? layout.height - row * cellH : cellH,
  };
}
